import React, { useState } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

import OAuthRegisterModalComponent from "./OAuthRegisterModalComponent";
import protectedAxios from "../../common/axios";
import { getRecaptchaToken } from "../../common/recaptcha";
import { API_URL, AUTH_PROVIDER } from "../../common/constants";
import Operations from "../../login/duck/operations";
import Utils from "../../common/utils";
import { validName } from "../../common/validationChecks";
import Actions from "../../login/duck/actions";

export function OAuthRegisterModalContainer() {
  const dispatch = useDispatch();

  const showModal = useSelector((state) => state.user.oAuthRegisterModal);
  const contactInfo = useSelector((state) => state.user.contactInfo);
  const authProvider = useSelector((state) => state.user.authProvider);
  const redirectTo = useSelector((state) => state.user.redirectTo);

  const setShowModal = (value) =>
    dispatch(Actions.showOAuthRegisterModal(value));

  const [userInfo, setUserInfo] = useState({
    firstName: contactInfo?.firstName || "",
    lastName: contactInfo?.lastName || "",
  });
  const [userInfoError, setUserInfoError] = useState({
    firstName: "",
    lastName: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const getRedirectPath = () => {
    // get redirect query param from URL
    const redirectQueryParam = new URLSearchParams(location.search).get(
      "redirect"
    );

    const redirectQueryFromRedux = redirectTo;

    if (redirectQueryParam) {
      return decodeURIComponent(redirectQueryParam);
    } else if (redirectQueryFromRedux) {
      return redirectQueryFromRedux;
    } else {
      return null;
    }
  };

  const handleOnChange = ({ key = null, value = null }) => {
    setUserInfo((userInfo) => {
      const _userInfo = {
        ...userInfo,
        [key]: value,
      };
      return _userInfo;
    });
  };

  const handleSignUp = () => {
    setIsLoading(true);
    if (!validName(userInfo.firstName)) {
      setUserInfoError((userInfoError) => ({
        ...userInfoError,
        firstName:
          "First name can't be empty and should only contain characters.",
      }));
      setIsLoading(false);
      return;
    }

    setUserInfoError((userInfoError) => ({
      ...userInfoError,
      firstName: "",
    }));

    const payload = {
      firstName: userInfo.firstName.trim(),
      lastName: userInfo.lastName.trim(),
      avatar: Utils.generateAvatar(
        `${userInfo.firstName ? userInfo.firstName.trim() : ""} ${
          userInfo.lastName ? userInfo.lastName.trim() : ""
        }`,
        contactInfo.phone.slice(-1)
      ),
    };

    let signupPath = "signup";

    // Phone-based providers must present the single-use proof handed out by
    // the verify step; the server refuses phone-only signups without it.
    if (authProvider === AUTH_PROVIDER.WHATSAPP) {
      payload["phone"] = contactInfo.phone;
      payload["authProvider"] = AUTH_PROVIDER.WHATSAPP;
      payload["phoneVerificationToken"] = contactInfo.phoneVerificationToken;
      signupPath = "whatsapp/signup";
    }

    if (authProvider === AUTH_PROVIDER.SMS) {
      payload["phone"] = contactInfo.phone;
      payload["authProvider"] = AUTH_PROVIDER.SMS;
      payload["phoneVerificationToken"] = contactInfo.phoneVerificationToken;
    }

    getRecaptchaToken("signup")
      .then((recaptchaToken) =>
        protectedAxios.post(`${API_URL}/v1/auth/${signupPath}`, {
          ...payload,
          ...(recaptchaToken && { recaptchaToken }),
        })
      )
      .then(({ data }) => {
        dispatch(
          Operations.loginUserSuccess(
            data.accessToken,
            data.refreshToken,
            data.profile,
            data?.isAccountReactivated || false
          )
        );
        window?.dataLayer?.push({
          event: "signup",
          firstName: data?.profile?.firstName,
          lastName: data?.profile?.lastName,
        });
        setShowModal(false);
        if (getRedirectPath()) {
          window.history.replaceState({}, null, getRedirectPath());
          window.location.href = getRedirectPath();
          dispatch(Actions.redirectAfterLogin(null));
        }
      })
      .catch((err) => {
        toast.error("Something went wrong please try again.");
        if (err.status === 400) {
          if (err.data.message[0].includes("firstName")) {
            setUserInfoError((userInfoError) => ({
              ...userInfoError,
              firstName: err.data.message[0],
            }));
          } else if (err.data.message[0].includes("lastName")) {
            setUserInfoError((userInfoError) => ({
              ...userInfoError,
              lastName: err.data.message[0],
            }));
          }
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <OAuthRegisterModalComponent
      {...{
        showModal,
        setShowModal,
        contactInfo,
        authProvider,
        handleSignUp,
        userInfoError,
        userInfo,
        handleOnChange,
        isLoading,
      }}
    />
  );
}
